import { formatMonth } from "../../creditos/dashboardData";

/**
 * Agregações puras da aba Dados. Nada aqui é premissa: tudo vem dos registros do SPED
 * devolvidos pela API, somado ou comparado.
 */

export const TAXES = [
  { key: "pis", label: "PIS", debit: "pisDebito", credit: "pisCredito", net: "pisLiquido", debitLabel: "Débito", creditLabel: "Crédito" },
  { key: "cof", label: "COFINS", debit: "cofDebito", credit: "cofCredito", net: "cofLiquido", debitLabel: "Débito", creditLabel: "Crédito" },
  { key: "icms", label: "ICMS", debit: "icmsSaidas", credit: "icmsCreditos", net: "icmsLiquido", debitLabel: "Saídas", creditLabel: "Créditos" },
];

// Espelha o mapa `fontes` de /reforma/fiscal-data/mensal, caso a resposta não o traga
export const FALLBACK_SOURCES = {
  faturamento: "C100 VL_DOC (saídas) · EFD ICMS/IPI",
  compras: "C100 VL_DOC (entradas) · EFD ICMS/IPI",
  pisDebito: "C100 VL_PIS (saídas) · EFD Contribuições",
  pisCredito: "C100 VL_PIS (entradas) · EFD Contribuições",
  pisLiquido: "PIS débito − crédito (C100)",
  cofDebito: "C100 VL_COFINS (saídas) · EFD Contribuições",
  cofCredito: "C100 VL_COFINS (entradas) · EFD Contribuições",
  cofLiquido: "COFINS débito − crédito (C100)",
  icmsSaidas: "C100 VL_ICMS (saídas) · EFD ICMS/IPI",
  icmsCreditos: "C100 VL_ICMS (entradas) · EFD ICMS/IPI",
  icmsLiquido: "ICMS saídas − créditos (C100)",
};

export const TABLE_GROUPS = [
  {
    key: "movimento",
    label: "Movimento",
    source: "C100 VL_DOC · EFD ICMS/IPI",
    columns: [{ key: "faturamento", label: "Faturamento" }, { key: "compras", label: "Compras" }],
  },
  ...TAXES.map((t) => ({
    key: t.key,
    label: t.label,
    source: { pis: "C100 VL_PIS · EFD Contribuições", cof: "C100 VL_COFINS · EFD Contribuições", icms: "C100 VL_ICMS · EFD ICMS/IPI" }[t.key],
    columns: [
      { key: t.debit, label: t.debitLabel },
      { key: t.credit, label: t.creditLabel },
      { key: t.net, label: "Líquido", strong: true },
    ],
  })),
];

const netOf = (row) => TAXES.reduce((acc, t) => acc + (row?.[t.net] || 0), 0);
const debitOf = (row) => TAXES.reduce((acc, t) => acc + (row?.[t.debit] || 0), 0);
const creditOf = (row) => TAXES.reduce((acc, t) => acc + (row?.[t.credit] || 0), 0);

const listNames = (names) => (names.length < 2 ? names.join("") : `${names.slice(0, -1).join(", ")} e ${names[names.length - 1]}`);

function median(values) {
  if (!values.length) return 0;
  const sorted = [...values].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  return sorted.length % 2 ? sorted[mid] : (sorted[mid - 1] + sorted[mid]) / 2;
}

// Mês sem SPED continua no eixo, com zero, para não esconder o buraco
function fillGaps(rows) {
  const sorted = [...rows].sort((a, b) => String(a.periodo).localeCompare(String(b.periodo)));
  if (sorted.length < 2 || !/^\d{4}-\d{2}$/.test(sorted[0].periodo)) return sorted.map((r) => ({ ...r, missing: false }));
  const byKey = new Map(sorted.map((r) => [r.periodo, r]));
  const [startYear, startMonth] = sorted[0].periodo.split("-").map(Number);
  const [endYear, endMonth] = sorted[sorted.length - 1].periodo.split("-").map(Number);
  const out = [];
  for (let i = startYear * 12 + startMonth - 1; i <= endYear * 12 + endMonth - 1; i += 1) {
    const key = `${Math.floor(i / 12)}-${String((i % 12) + 1).padStart(2, "0")}`;
    out.push(byKey.has(key) ? { ...byKey.get(key), missing: false } : { periodo: key, missing: true });
  }
  return out;
}

export function scopeOf(row) {
  const debit = debitOf(row);
  const credit = creditOf(row);
  const net = netOf(row);
  const revenue = row?.faturamento || 0;
  return {
    revenue,
    purchases: row?.compras || 0,
    debit,
    credit,
    net,
    netShare: revenue ? (net / revenue) * 100 : 0,
    purchasesShare: revenue ? ((row?.compras || 0) / revenue) * 100 : 0,
    coverage: debit ? (credit / debit) * 100 : 0,
    lines: TAXES.map((t) => ({
      ...t,
      debitValue: row?.[t.debit] || 0,
      creditValue: row?.[t.credit] || 0,
      netValue: row?.[t.net] || 0,
    })),
  };
}

function buildReadings(months) {
  const real = months.filter((m) => !m.missing);
  const readings = [];

  const above = real.filter((m) => (m.compras || 0) > (m.faturamento || 0));
  if (above.length > 0) {
    const widest = above.reduce((a, b) => ((b.compras - b.faturamento) > (a.compras - a.faturamento) ? b : a));
    readings.push({
      key: "above",
      count: above.length,
      of: real.length,
      caption: "meses",
      title: "Compras acima do faturamento",
      money: widest.compras - widest.faturamento,
      where: widest.periodo,
      sub: "Maior diferença",
    });
  }

  const creditor = real.filter((m) => TAXES.some((t) => (m[t.net] || 0) < 0));
  if (creditor.length > 0) {
    const names = TAXES.filter((t) => creditor.some((m) => (m[t.net] || 0) < 0)).map((t) => t.label);
    readings.push({
      key: "creditor",
      count: creditor.length,
      of: real.length,
      caption: "meses",
      title: `${listNames(names)} com saldo credor`,
      where: creditor[0].periodo,
      sub: creditor.length <= 3
        ? `${listNames(creditor.map((m) => formatMonth(m.periodo)))}: o crédito das entradas superou o débito das saídas.`
        : `O crédito das entradas superou o débito das saídas. O primeiro foi ${formatMonth(creditor[0].periodo)}.`,
    });
  }

  if (real.length >= 3) {
    const peak = real.reduce((a, b) => (netOf(b) > netOf(a) ? b : a));
    const typical = median(real.map(netOf));
    if (typical > 0) {
      readings.push({
        key: "peak",
        times: netOf(peak) / typical,
        caption: "o mês típico",
        title: `${formatMonth(peak.periodo)} é o pico de tributo líquido`,
        where: peak.periodo,
        peak: netOf(peak),
        typical,
      });
    }
  }

  return readings;
}

export function buildMonthly({ dadosMensais, totalMensal, fontes }) {
  const months = fillGaps(dadosMensais || []).map((m) => ({ ...m, net: netOf(m) }));
  const real = months.filter((m) => !m.missing);
  const missing = months.filter((m) => m.missing);
  const maxNet = TAXES.reduce((acc, t) => ({
    ...acc,
    [t.key]: Math.max(1, ...real.map((m) => Math.abs(m[t.net] || 0))),
  }), {});

  return {
    months,
    total: totalMensal,
    sources: { ...FALLBACK_SOURCES, ...(fontes || {}) },
    maxNet,
    readings: buildReadings(months),
    coverage: {
      count: real.length,
      first: real[0]?.periodo,
      last: real[real.length - 1]?.periodo,
      missing: missing.map((m) => m.periodo),
    },
  };
}

// A API manda rótulos em caixa alta; na tela só as siglas continuam assim
export function sentenceCase(text = "") {
  if (text !== text.toUpperCase()) return text;
  const lower = text.toLowerCase().replace(/\b(sped|m\d{3}|cbs|ibs|pis|cofins|icms|ecd)\b/g, (m) => m.toUpperCase());
  return lower.charAt(0).toUpperCase() + lower.slice(1);
}

export function buildCredits(creditos) {
  if (!creditos) return null;
  const compare = creditos.comparativoCredito || {};
  const total = creditos.totalCreditoConfirmado || {};
  const detail = compare.atualDetalhado || {};
  const base = total.valorAnual || 0;
  const rateOf = (v) => (base ? (v / base) * 100 : 0);
  const isReal = detail.metodologia === "real";

  const accounts = [...(creditos.contasElegiveis || [])]
    .sort((a, b) => (b.saldo2025 || 0) - (a.saldo2025 || 0))
    .map((c) => ({
      ...c,
      group: sentenceCase(c.agrupamento || ""),
      share: base ? ((c.saldo2025 || 0) / base) * 100 : 0,
    }));

  const groups = [...accounts.reduce((map, c) => {
    map.set(c.group, (map.get(c.group) || 0) + (c.saldo2025 || 0));
    return map;
  }, new Map())]
    .map(([label, value]) => ({ label, value, share: base ? (value / base) * 100 : 0 }))
    .sort((a, b) => b.value - a.value);

  return {
    base,
    isReal,
    detail,
    today: compare.atualEstimado || 0,
    future: compare.futuroTotal || 0,
    gain: compare.ganhoAdicional || 0,
    captions: { today: compare.atualCaption, future: compare.futuroCaption, gain: compare.ganhoCaption },
    cbs: total.creditoCBS || 0,
    ibs: total.creditoIBS || 0,
    rates: {
      today: rateOf(compare.atualEstimado || 0),
      future: rateOf(compare.futuroTotal || 0),
      cbs: rateOf(total.creditoCBS || 0),
      ibs: rateOf(total.creditoIBS || 0),
    },
    multiplier: compare.atualEstimado ? (compare.futuroTotal || 0) / compare.atualEstimado : 0,
    summary: (creditos.resumoCredito || []).map((r) => ({
      ...r,
      label: sentenceCase(r.classificacao || ""),
    })),
    summaryTotal: { ...total, label: sentenceCase(total.classificacao || "Total") },
    accounts,
    groups,
    accountsTotal: {
      saldo: accounts.reduce((acc, c) => acc + (c.saldo2025 || 0), 0),
      credit: accounts.reduce((acc, c) => acc + (c.creditoCbsIbs || 0), 0),
      baseCofins: accounts.reduce((acc, c) => acc + (c.baseCofins || 0), 0),
    },
  };
}
